import { PlusOutlined, PushpinOutlined } from "@ant-design/icons";
import {
	Button,
	Form,
	Input,
	message,
	Select,
	Space,
	Switch,
	Table,
	Tag,
	Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AdminPage } from "../../components/AdminPage";
import { formatAdminDateTimeJst } from "../../lib/format";
import { getAccessToken } from "../../lib/staff";
import { getTrpcClient } from "../../lib/trpc";

type AnnouncementCategory =
	| "maintenance"
	| "new_product"
	| "campaign"
	| "notice"
	| "community";

type OfficialAccountRow = {
	createdAt: string;
	displayName: string | null;
	userId: string;
};

type AnnouncementRow = {
	announcementCategory: AnnouncementCategory;
	authorDisplayName: string | null;
	authorUserId: string;
	body: string;
	createdAt: string;
	id: string;
	isPinned: boolean;
	updatedAt: string;
};

const CATEGORY_OPTIONS: { label: string; value: AnnouncementCategory }[] = [
	{ value: "maintenance", label: "メンテナンス" },
	{ value: "new_product", label: "新商品" },
	{ value: "campaign", label: "キャンペーン" },
	{ value: "notice", label: "お知らせ" },
	{ value: "community", label: "コミュニティ" },
];

const CATEGORY_LABEL: Record<AnnouncementCategory, string> = {
	campaign: "キャンペーン",
	community: "コミュニティ",
	maintenance: "メンテナンス",
	new_product: "新商品",
	notice: "お知らせ",
};

type CreateFormValues = {
	announcementCategory: AnnouncementCategory;
	authorUserId: string;
	body: string;
	pinOnCreate: boolean;
};

export function PostsListPage() {
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [rows, setRows] = useState<AnnouncementRow[]>([]);
	const [accounts, setAccounts] = useState<OfficialAccountRow[]>([]);
	const [canWrite, setCanWrite] = useState(false);
	const [form] = Form.useForm<CreateFormValues>();

	const accountOptions = useMemo(
		() =>
			accounts.map((account) => ({
				label: account.displayName
					? `${account.displayName} (${account.userId.slice(0, 8)}…)`
					: account.userId,
				value: account.userId,
			})),
		[accounts],
	);

	const fetchData = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const accessToken = await getAccessToken();
			if (!accessToken) throw new Error("サインインが必要です。");
			const trpc = getTrpcClient(accessToken);
			const [whoami, accountList, announcementList] = await Promise.all([
				trpc.admin.whoami.query(),
				trpc.admin.listOfficialAccounts.query(),
				trpc.admin.listOfficialAnnouncements.query({ limit: 100 }),
			]);
			setCanWrite(whoami.role === "admin");
			setAccounts(accountList.items);
			setRows(announcementList.items);
		} catch (fetchError) {
			setError(
				fetchError instanceof Error
					? fetchError.message
					: "公式お知らせの取得に失敗しました。",
			);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		void fetchData();
	}, [fetchData]);

	async function handleCreate(values: CreateFormValues) {
		setSaving(true);
		try {
			const accessToken = await getAccessToken();
			if (!accessToken) throw new Error("サインインが必要です。");
			await getTrpcClient(accessToken).admin.createOfficialAnnouncement.mutate({
				announcementCategory: values.announcementCategory,
				authorUserId: values.authorUserId,
				body: values.body.trim(),
				pinOnCreate: values.pinOnCreate,
			});
			message.success("公式お知らせを公開しました。");
			form.resetFields();
			form.setFieldsValue({
				announcementCategory: "notice",
				pinOnCreate: false,
			});
			await fetchData();
		} catch (createError) {
			message.error(
				createError instanceof Error
					? createError.message
					: "公式お知らせの作成に失敗しました。",
			);
		} finally {
			setSaving(false);
		}
	}

	async function togglePin(row: AnnouncementRow) {
		if (!canWrite) return;
		try {
			const accessToken = await getAccessToken();
			if (!accessToken) throw new Error("サインインが必要です。");
			await getTrpcClient(
				accessToken,
			).admin.setOfficialAnnouncementPinned.mutate({
				isPinned: !row.isPinned,
				postId: row.id,
			});
			message.success(
				row.isPinned ? "ピン留めを解除しました。" : "ピン留めしました。",
			);
			await fetchData();
		} catch (pinError) {
			message.error(
				pinError instanceof Error
					? pinError.message
					: "ピン留めの更新に失敗しました。",
			);
		}
	}

	const columns: ColumnsType<AnnouncementRow> = [
		{
			title: "公開日時",
			dataIndex: "createdAt",
			width: 180,
			render: (value: string) => formatAdminDateTimeJst(value),
		},
		{
			title: "カテゴリ",
			dataIndex: "announcementCategory",
			width: 120,
			render: (value: AnnouncementCategory) => (
				<Tag color="blue">{CATEGORY_LABEL[value]}</Tag>
			),
		},
		{
			title: "公式アカウント",
			dataIndex: "authorDisplayName",
			width: 160,
			render: (value: string | null, row) => value ?? row.authorUserId,
		},
		{
			title: "本文",
			dataIndex: "body",
			ellipsis: true,
		},
		{
			title: "ピン",
			dataIndex: "isPinned",
			width: 100,
			render: (value: boolean, row) =>
				canWrite ? (
					<Button
						type={value ? "primary" : "default"}
						size="small"
						icon={<PushpinOutlined />}
						onClick={() => void togglePin(row)}
					>
						{value ? "解除" : "固定"}
					</Button>
				) : value ? (
					<Tag color="gold">固定中</Tag>
				) : (
					"—"
				),
		},
	];

	return (
		<AdminPage
			title="公式お知らせ管理"
			subtitle="登録済み公式アカウントから本文付きお知らせを作成・一覧表示します。"
		>
			{error ? (
				<Typography.Paragraph type="danger">{error}</Typography.Paragraph>
			) : null}

			{canWrite ? (
				<Form
					form={form}
					layout="vertical"
					initialValues={{
						announcementCategory: "notice",
						pinOnCreate: false,
					}}
					onFinish={(values) => void handleCreate(values)}
					style={{ marginBottom: 24, maxWidth: 720 }}
				>
					<Form.Item
						label="公式アカウント"
						name="authorUserId"
						rules={[
							{ required: true, message: "公式アカウントを選択してください。" },
						]}
					>
						<Select
							options={accountOptions}
							placeholder="投稿者を選択"
							showSearch
							optionFilterProp="label"
						/>
					</Form.Item>
					<Form.Item
						label="カテゴリ"
						name="announcementCategory"
						rules={[
							{ required: true, message: "カテゴリを選択してください。" },
						]}
					>
						<Select options={CATEGORY_OPTIONS} />
					</Form.Item>
					<Form.Item
						label="本文"
						name="body"
						rules={[
							{ required: true, message: "本文を入力してください。" },
							{ max: 1000, message: "本文は1000文字以内です。" },
						]}
					>
						<Input.TextArea rows={4} showCount maxLength={1000} />
					</Form.Item>
					<Form.Item
						label="公開と同時にピン留め"
						name="pinOnCreate"
						valuePropName="checked"
					>
						<Switch />
					</Form.Item>
					<Space>
						<Button
							type="primary"
							htmlType="submit"
							icon={<PlusOutlined />}
							loading={saving}
							disabled={accounts.length === 0}
						>
							公開する
						</Button>
					</Space>
				</Form>
			) : (
				<Typography.Paragraph type="secondary">
					閲覧専用ロールのため、新規作成・ピン留め操作は利用できません。
				</Typography.Paragraph>
			)}

			<Table<AnnouncementRow>
				rowKey="id"
				loading={loading}
				columns={columns}
				dataSource={rows}
				pagination={{ pageSize: 20, showSizeChanger: false }}
			/>
		</AdminPage>
	);
}
